import type { CanvasSelector } from '@hector/shared-types';
import { normalizeSearchText } from '@hector/agent-text-normalizer';
import { lireLaForme } from '@hector/docx-forme';
import { readCanvasSnapshot, type CanvasSnapshot, type CanvasSnapshotBlock } from './canvasSnapshot';
import { exporterLActeEnMemoire } from './exportDeLActe';
import type { SuperDocDocumentApi } from './superDocDocumentApi';

const SELECTOR_PAGE_SIZE = 250;
export const MAX_ACTIONABLE_SELECTOR_CANDIDATES = 8;

type SelectorResolution =
  | { ok: true; target: CanvasSnapshotBlock; candidateCount: number }
  | { ok: false; code: 'SELECTOR_NOT_FOUND'; candidateCount: number }
  | { ok: false; code: 'INVALID_SELECTOR_TERM'; candidateCount: 0 }
  | { ok: false; code: 'TARGET_NOT_IN_FINAL_INSPECTION'; candidateCount: number }
  | {
    ok: false;
    code: 'AMBIGUOUS_SELECTOR';
    candidateCount: number;
    /** First candidates in document order, bounded for an actionable model error. */
    candidates: Array<Pick<CanvasSnapshotBlock, 'nodeId' | 'ordinal' | 'text'>>;
  };

/**
 * A native final projection may be readable without assigning a mutation-grade
 * public identity to every fragment. `undefined` survives only for isolated
 * internal callers: public action envelopes without that proof are refused at
 * both frontend action boundaries. An empty Set deliberately refuses every raw
 * target.
 */
export function isTargetInFinalInspection(
  target: CanvasSnapshotBlock,
  inspectedPublicNodeIds: ReadonlySet<string> | undefined,
): boolean {
  return inspectedPublicNodeIds === undefined || inspectedPublicNodeIds.has(target.nodeId);
}

/**
 * A range mutation is a mutation of every expanded raw block, not merely of
 * the selectors used to delimit it. Keeping that policy here prevents an
 * unavailable block between two public endpoints from becoming an accidental
 * destructive target (#1605).
 */
export function areTargetsInFinalInspection(
  targets: readonly CanvasSnapshotBlock[],
  inspectedPublicNodeIds: ReadonlySet<string> | undefined,
): boolean {
  return targets.every((target) => isTargetInFinalInspection(target, inspectedPublicNodeIds));
}

/**
 * A provider range is only safe when its raw window contains exactly the
 * normalized, consecutively addressed public blocks that will be mutated.
 *
 * `readCanvasSnapshot` deliberately omits malformed provider entries from a
 * model receipt. That is correct for reading, but a provider range could still
 * include such an entry. A modern final projection must fail closed instead of
 * treating an omitted raw interior as an implicitly public target.
 */
export function isCompletePublicRange(
  snapshot: Pick<CanvasSnapshot, 'blocks' | 'rawBlocksCount'>,
  firstOrdinal: number,
  lastOrdinal: number,
  inspectedPublicNodeIds: ReadonlySet<string> | undefined,
): boolean {
  if (inspectedPublicNodeIds === undefined) return true;
  const expectedCount = lastOrdinal - firstOrdinal + 1;
  return snapshot.rawBlocksCount === expectedCount
    && snapshot.blocks.length === expectedCount
    && snapshot.blocks.every((block, index) => block.ordinal === firstOrdinal + index && inspectedPublicNodeIds.has(block.nodeId));
}

/** Deliberately does not disclose which raw range member lacks a public identity. */
export function messageRangeNotInFinalInspection(): string {
  return 'canvas_perform_action refused the requested range before mutating the document: one or more blocks in its raw interval cannot be proven to carry a public identity in the latest native final projection. Nothing was modified. Run a fresh, targeted canvas_inspect with projection="final" and select only a fully public range.';
}

/** The recovery path intentionally does not leak a raw non-public node id. */
export function messageTargetNotInFinalInspection(): string {
  return 'canvas_perform_action refused the resolved target before mutating the document: the latest native final projection made this passage readable but did not publish a public mutation identity for it. Nothing was modified. Run a fresh, targeted canvas_inspect with projection="final" and use only a block carrying a public nodeId.';
}

/** Longueur maximale d'un texte cherché recopié dans un refus : assez pour le reconnaître, jamais un paragraphe. */
const LONGUEUR_MAX_DU_TEXTE_CITE = 80;

/** Le texte cherché tel qu'un refus le cite : entre guillemets, borné. */
export function citerLeTexteCherche(texte: string): string {
  return JSON.stringify(texte.length > LONGUEUR_MAX_DU_TEXTE_CITE ? `${texte.slice(0, LONGUEUR_MAX_DU_TEXTE_CITE)}…` : texte);
}

/**
 * La phrase qui dit qu'un texte cherché porte des marques Markdown, ou rien.
 *
 * WHY : le modèle écrit `# Titre` ou `**mot**` dans ce qu'il cherche, quand le document ne porte que le texte. La
 * recherche échouait sans dire pourquoi (conversation de production du 2026-10-13).
 */
export function avisDeMarquesMarkdown(cherches: readonly string[]): string {
  return cherches.some((cherche) => /^\s*#/u.test(cherche) || cherche.includes('**') || cherche.includes('__'))
    ? ' The search runs on the plain text of the document, without Markdown marks: remove the leading # and the ** or __ around words, they are not part of the text.'
    : '';
}

/**
 * Le refus d'un sélecteur de bloc que le document ne résout pas : ce qui était cherché, puis la suite.
 *
 * @param intention Le verbe refusé, tel que le modèle l'a appelé.
 * @param candidateCount Les blocs que le sélecteur apparie : non nul quand seule l'`occurrence` demandée n'existe pas.
 */
export function messageSelecteurIntrouvable(intention: string, selector: CanvasSelector, candidateCount: number): string {
  const relire = 'canvas_inspect with selector:{kind:"textSearch",terms:["<one short distinctive word of the passage>"],match:"all"}';
  if (selector.kind === 'nodeId') {
    return `${intention} was refused before any write: no block of the current document has nodeId=${JSON.stringify(selector.nodeId)}. `
      + 'A nodeId is copied as is from a canvas_inspect of THIS document, and a block deleted or replaced since then no longer answers to it. Nothing was written. '
      + `Next step: ${relire}, then resend with the nodeId it returns.`;
  }
  if (selector.kind === 'ordinal') {
    return `${intention} was refused before any write: no block answers to ordinal ${selector.value} — the editor no longer publishes ranks, so an ordinal selector designates nothing. Nothing was written. `
      + `Next step: ${relire}, then resend with the nodeId it returns.`;
  }
  const termes = selector.terms.map(citerLeTexteCherche).join(', ');
  const cherche = `${selector.match === 'any' ? 'any of' : 'all of'} the term(s) ${termes}`;
  const constat = candidateCount > 0 && selector.occurrence !== undefined
    ? `${candidateCount} block(s) contain ${cherche}, so occurrence ${selector.occurrence} does not exist`
    : `no block of the current document contains ${cherche}`;
  return `${intention} was refused before any write: ${constat}. Nothing was written.${avisDeMarquesMarkdown(selector.terms)} `
    + `Next step: ${relire}, then resend with the nodeId it returns, or with terms copied from the text it shows.`;
}

/**
 * ⚠️ **`match` est une conjonction de TERMES, pas un compte d'occurrences.**
 * `'all'` veut dire « tous les termes présents dans le même bloc », `'any'`
 * « au moins un ». Le même mot-clé signifie « toutes les occurrences » sur
 * l'agent d'édition Word — c'est le contresens qui a fait écrire `match:"all"`
 * à l'agent en croyant demander un remplacement global (#1010).
 *
 * Les deux côtés de la comparaison passent par la MÊME normalisation (#1011) :
 * comparer un document normalisé à une requête brute rendrait la normalisation
 * inopérante dès que la requête porte une apostrophe courbe.
 */
function matchesText(block: CanvasSnapshotBlock, selector: Extract<CanvasSelector, { kind: 'textSearch' }>): boolean {
  const text = normalizeSearchText(block.fullText);
  const terms = selector.terms.map(normalizeSearchText);
  return selector.match === 'any' ? terms.some((term) => text.includes(term)) : terms.every((term) => text.includes(term));
}

function aUnTermeVideApresNormalisation(selector: Extract<CanvasSelector, { kind: 'textSearch' }>): boolean {
  return selector.terms.some((term) => normalizeSearchText(term).length === 0);
}

async function listBlocks(doc: SuperDocDocumentApi): Promise<CanvasSnapshotBlock[]> {
  const blocks: CanvasSnapshotBlock[] = [];
  for (let offset = 0; ; offset += SELECTOR_PAGE_SIZE) {
    const page = await readCanvasSnapshot(doc, { offset, limit: SELECTOR_PAGE_SIZE });
    blocks.push(...page.blocks);
    // A malformed provider block is excluded from normalized candidates, but it
    // still occupies a provider page slot. Stopping on normalized length could
    // hide a duplicate destructive target on the next raw page.
    if (page.rawBlocksCount < SELECTOR_PAGE_SIZE) return blocks;
  }
}

/** L'adresse d'un paragraphe de cellule, telle que l'éditeur l'accepte dans `within`. */
type AdresseDeParagraphe = { nodeId: string; nodeType: 'paragraph' };

/**
 * Un paragraphe de cellule résolu : `adresse`, où l'éditeur cherche et écrit ; `tableau`, le bloc de la liste qui le
 * contient, seul à porter son texte dans une relecture.
 * - `hors_lecture` : la dernière lecture ne l'a pas publié, ou l'éditeur ne sait pas chercher : rien n'est établi.
 * - `texte_absent` : le paragraphe, dans une cellule d'un tableau du corps, ne porte pas le texte cherché.
 * - `hors_tableau` : il le porte, et aucun tableau du corps ne le contient (zone de texte, forme).
 * - `hors_cellule` : il ne porte pas le texte cherché, et aucune cellule du corps n'est ce paragraphe — supprimé depuis
 *   la lecture, ou dans une zone de texte.
 */
export type ParagrapheDeCelluleResolu =
  | { ok: true; adresse: AdresseDeParagraphe; tableau: CanvasSnapshotBlock }
  | { ok: false; raison: 'hors_lecture' | 'texte_absent' | 'hors_tableau' | 'hors_cellule' };

/**
 * Vrai quand le fichier exporté porte un paragraphe `nodeId` dans une cellule d'un tableau du corps. Faux aussi quand
 * le fichier ne se lit pas : rien n'établit alors la cellule.
 *
 * WHY : un nodeId publié par la dernière lecture et absent de la liste des blocs n'est pas forcément une cellule — un
 * paragraphe du corps supprimé depuis l'est aussi. Le refus l'appelait « cellule de tableau » et renvoyait l'agent
 * vers une écriture par passage qui échouait à son tour (revue du 2026-10-16). Le fichier est la source qui situe une
 * cellule pour `rewrite_block` (`enCellule`, `@hector/docx-forme`).
 */
export async function estUnParagrapheDeCelluleAuFichier(doc: SuperDocDocumentApi, nodeId: string): Promise<boolean> {
  try {
    const forme = await lireLaForme(await exporterLActeEnMemoire(doc));
    return forme.paragraphesDeTableau.some((cellule) => cellule.nodeId === nodeId);
  } catch {
    return false;
  }
}

/** Les paragraphes où `query.match` situe chaque passage trouvé (`items[].address.nodeId`). */
async function paragraphesDesPassages(doc: SuperDocDocumentApi, pattern: string, within: { nodeId: string; nodeType: string }): Promise<string[]> {
  const rendu: unknown = await doc.query?.match?.({ type: 'text', pattern, within: { kind: 'block', nodeType: within.nodeType, nodeId: within.nodeId } });
  const items: unknown = typeof rendu === 'object' && rendu !== null ? (rendu as { items?: unknown }).items : undefined;
  if (!Array.isArray(items)) return [];
  return items.flatMap((item: unknown) => {
    const adresse: unknown = typeof item === 'object' && item !== null ? (item as { address?: unknown }).address : undefined;
    const nodeId: unknown = typeof adresse === 'object' && adresse !== null ? (adresse as { nodeId?: unknown }).nodeId : undefined;
    return typeof nodeId === 'string' ? [nodeId] : [];
  });
}

/**
 * Résout un `nodeId` que la liste des blocs ne porte pas vers le paragraphe de cellule qu'il désigne et son tableau.
 *
 * WHY : `canvas_inspect` publie les paragraphes de cellule, mais `blocks.list` rend un tableau comme UN bloc. Un
 * `scope` ou un `selector` qui nommait une cellule rendait `SELECTOR_NOT_FOUND` d'un identifiant que l'agent venait de
 * lire (conversation de production du 2026-10-13 ; `tests/ws-mocked/mesure-recus-vrais.spec.ts`, mesure E).
 *
 * Mesuré le 2026-10-13 sur l'éditeur réel (`tests/ws-mocked/canvas-recus-vrais-cellule-de-tableau.spec.ts`) :
 * `query.match` et l'aperçu d'un plan acceptent `within:{kind:'block',nodeType:'paragraph',nodeId:<cellule>}`, tableau
 * imbriqué compris, et y BORNENT la recherche — un texte présent dans deux cellules (`AMBIGUOUS_MATCH` quand on vise
 * le tableau) est trouvé une fois dans la cellule visée. Chaque passage trouvé dans un tableau nomme son paragraphe
 * (`address.nodeId`) : c'est ce qui rattache la cellule à son tableau.
 *
 * @param cherche Le texte que l'appel cherche dans ce paragraphe, tel que l'agent l'a écrit.
 */
export async function resoudreUnParagrapheDeCellule(
  doc: SuperDocDocumentApi,
  nodeId: string,
  cherche: string,
  inspectedPublicNodeIds: ReadonlySet<string> | undefined,
): Promise<ParagrapheDeCelluleResolu> {
  if (typeof doc.query?.match !== 'function' || (inspectedPublicNodeIds !== undefined && !inspectedPublicNodeIds.has(nodeId))) {
    return { ok: false, raison: 'hors_lecture' };
  }
  const adresse: AdresseDeParagraphe = { nodeId, nodeType: 'paragraph' };
  let dansLeParagraphe: string[];
  try {
    dansLeParagraphe = await paragraphesDesPassages(doc, cherche, adresse);
  } catch {
    // L'éditeur ne connaît pas cette adresse : l'appelant garde son refus d'identifiant introuvable.
    return { ok: false, raison: 'hors_lecture' };
  }
  if (!dansLeParagraphe.includes(nodeId)) return { ok: false, raison: await estUnParagrapheDeCelluleAuFichier(doc, nodeId) ? 'texte_absent' : 'hors_cellule' };
  for (const tableau of (await listBlocks(doc)).filter((block) => block.nodeType === 'table')) {
    if ((await paragraphesDesPassages(doc, cherche, tableau)).includes(nodeId)) return { ok: true, adresse, tableau };
  }
  return { ok: false, raison: 'hors_tableau' };
}

/**
 * Le refus d'un nodeId publié par la lecture et absent de la liste des blocs : ce qui était cherché, où, et la suite.
 *
 * @param constat Le texte cherché et pourquoi il n'est pas écrit ; `bloc_entier` : l'appel adresse une cellule (établie
 *   au fichier) comme un bloc entier (`text_insert`) ; `hors_cellule` : aucune cellule du corps n'est ce paragraphe.
 */
export function messageParagrapheDeCelluleNonResolu(
  intention: string,
  nodeId: string,
  constat: { cherche: string; raison: 'texte_absent' | 'hors_tableau' } | 'bloc_entier' | 'hors_cellule',
): string {
  const debut = `${intention} was refused before any write: nodeId=${nodeId} is not a block of the document list`;
  const enCellule = `${debut} — it is a paragraph inside a table cell.`;
  if (constat === 'hors_cellule') {
    return `${debut}, and no table of the document contains a paragraph with this nodeId: the paragraph your last reading published no longer exists in the document `
      + '(deleted or replaced since that reading), unless it sits in a text box or a shape, where no verb is verified to write. Nothing was written. '
      + 'Next step: read the document again with canvas_inspect, then resend with a nodeId that reading publishes.';
  }
  if (constat === 'bloc_entier') {
    return `${enCellule} This step addresses a whole block, which such a paragraph is not. Nothing was written. `
      + `Next step: change the text of that paragraph by passage — a text_rewrite step {find, replace, selector:{kind:"nodeId",nodeId:"${nodeId}"}}, `
      + `or replace_text with edits:[{find, replace}] and scope:{kind:"nodeId",nodeId:"${nodeId}"} — where find is a passage of its current text.`;
  }
  const { cherche, raison } = constat;
  if (raison === 'hors_tableau') {
    return `${debut}. It contains ${citerLeTexteCherche(cherche)}, but no table of the document body contains that paragraph: it sits in a text box or a shape, where no verb is verified to write. `
      + 'Nothing was written, and reading again will not change it. Tell the lawyer the text to change there.';
  }
  return `${enCellule} It does not contain ${citerLeTexteCherche(cherche)} (searched as typed: inside a cell, letter case may count, and curly apostrophes and non-breaking spaces are not folded). Nothing was written.`
    + `${avisDeMarquesMarkdown([cherche])} The search is bounded to that ONE cell paragraph: a text of a neighbouring cell is never found from it. `
    + `Next step: copy the passage exactly, letter case included, from the text canvas_inspect shows for nodeId=${nodeId} and resend with the same nodeId; if the passage is in another cell, use that cell paragraph's nodeId.`;
}

/**
 * TOUS les blocs du document qu'un sélecteur apparie, dans l'ordre du document.
 *
 * Extrait de `resolveCanvasSelector` pour #1009 : l'inspection a besoin de
 * l'ensemble des appariements (le modèle veut voir ses cibles), là où une
 * mutation n'en prend qu'une. Un second balayage écrit à côté divergerait au
 * premier changement de règle de correspondance — et c'est précisément la
 * divergence que #1011 décrit comme le pire mode d'échec : un moteur qui
 * TROUVE ce qu'un autre ne sait pas ATTEINDRE.
 *
 * ⚠️ Le balayage porte sur le document ENTIER (paginé par 250), jamais sur la
 * fenêtre demandée à l'inspection : une cible au 96ᵉ bloc doit être trouvée
 * alors que la fenêtre par défaut s'arrête au 80ᵉ.
 */
export async function listerLesBlocsApparies(
  doc: SuperDocDocumentApi,
  selector: CanvasSelector,
): Promise<CanvasSnapshotBlock[]> {
  // Zod rejects this on normal tool traffic, but this public resolver is also
  // callable by runtime code that may receive an injected object. `''.includes`
  // is true for every string, so allowing it would turn occurrence:2 into a
  // destructive second-block selector.
  if (selector.kind === 'textSearch' && aUnTermeVideApresNormalisation(selector)) return [];
  const blocks = await listBlocks(doc);
  return selector.kind === 'nodeId'
    ? blocks.filter((block) => block.nodeId === selector.nodeId)
    : selector.kind === 'ordinal'
      ? blocks.filter((block) => block.ordinal === selector.value && (selector.ordinalKind !== 'heading' || block.nodeType === 'heading' || block.headingLevel !== undefined))
      // Do not use `doc.query.match` ids as a candidate gate here: provider
      // indexes can be stale or partial, whereas this normalized full-document
      // scan is the model-facing source of truth for ambiguity and occurrence.
      : blocks.filter((block) => matchesText(block, selector));
}

/**
 * Resolves the selectors shared by inspection and mutations. Text search is
 * evaluated from the normalized full-document snapshot, so provider query
 * match ids can never hide duplicate destructive targets.
 */
export async function resolveCanvasSelector(
  doc: SuperDocDocumentApi,
  selector: CanvasSelector,
  options: { requireUnique: boolean; inspectedPublicNodeIds?: ReadonlySet<string> },
): Promise<SelectorResolution> {
  if (selector.kind === 'textSearch' && aUnTermeVideApresNormalisation(selector)) {
    return { ok: false, code: 'INVALID_SELECTOR_TERM', candidateCount: 0 };
  }
  const candidates = await listerLesBlocsApparies(doc, selector);

  if (candidates.length === 0) return { ok: false, code: 'SELECTOR_NOT_FOUND', candidateCount: 0 };
  if (options.requireUnique && selector.kind === 'textSearch' && selector.occurrence === undefined && candidates.length !== 1) {
    return {
      ok: false,
      code: 'AMBIGUOUS_SELECTOR',
      candidateCount: candidates.length,
      // The model needs concrete identifiers to recover, but emitting every
      // duplicate can turn a refusal into an unbounded tool result. Inspection
      // remains the path that pages through the complete match set (#1009).
      candidates: candidates.slice(0, MAX_ACTIONABLE_SELECTOR_CANDIDATES).map(({ nodeId, ordinal, text }) => ({ nodeId, ordinal, text })),
    };
  }

  const target = candidates[selector.kind === 'textSearch' && selector.occurrence !== undefined ? selector.occurrence - 1 : 0];
  if (target === undefined) return { ok: false, code: 'SELECTOR_NOT_FOUND', candidateCount: candidates.length };
  // Resolve ambiguity and 1-based occurrence in the raw document FIRST.
  // Filtering candidates before this check could make occurrence=1 silently
  // retarget an unavailable first match onto a later public block.
  if (!isTargetInFinalInspection(target, options.inspectedPublicNodeIds)) {
    return { ok: false, code: 'TARGET_NOT_IN_FINAL_INSPECTION', candidateCount: candidates.length };
  }
  return { ok: true, target, candidateCount: candidates.length };
}
